import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import auth from '../../../../services/authService';
import { PageBarHead } from '../../../../components/PageBar';
import SongBadges from '../../../../components/common/SongBadges';
import SongChartsBadge, { hasChartsBadge } from '../SongChartsBadge';
import SongDetails from '../SongDetails';
import ViewSongPluginsPerSong from '../ViewSongPluginsPerSong';
import { useGlobalContext } from '../../../../context/GlobalContext';
import { ReactComponent as CreateIcon } from '../../../../icons/page-edit.svg';
import { ReactComponent as PuzzleIcon } from '../../../../icons/puzzle.svg';

import './SongHeaderSection.scss';

// A song after the first in continuous view, headed like the first: a copy of the app bar with its
// title and buttons, then the same details card
const SongHeaderSection = ({
  song,
  songKey,
  songNumber,
  songIndex,
  fromUrl,
  onKeyChange,
  resetAutoTranspose,
  onManualTranspose,
}) => {
  const { t } = useTranslation();
  const [currentSongKey, setCurrentSongKey] = useState(songKey);
  const [showPlugins, setShowPlugins] = useState(false);
  const history = useHistory();
  const { isScrolling } = useGlobalContext();
  const user = auth.getCurrentUser();
  const canEditSong = Boolean(user?.isAdmin && user.teamId === song.team._id);

  // Sync local key when prop changes (e.g., after reset)
  useEffect(() => {
    if (songKey !== currentSongKey) {
      setCurrentSongKey(songKey);
    }
  }, [songKey, currentSongKey]);

  const handleLocalKeyChange = (newKey) => {
    setCurrentSongKey(newKey);
    onKeyChange?.(newKey);
  };

  const handleEditSong = () => {
    history.push({ pathname: `/songs/${song._id}`, state: { from: fromUrl } });
  };

  return (
    <div className="SongHeaderSection">
      <div className="song-bar">
        <PageBarHead
          title={`#${songNumber} ${song.title}`}
          subtitle={song.artist}
          actions={[
            ...(canEditSong ? [{ label: t('view.editSong'), icon: <CreateIcon />, onClick: handleEditSong }] : []),
            {
              label: t('view.plugins'),
              icon: <PuzzleIcon />,
              pressed: showPlugins,
              disabled: isScrolling,
              onClick: () => setShowPlugins((prev) => !prev),
            },
          ]}
        />
      </div>

      <SongDetails song={song} songKey={currentSongKey} resetAutoTranspose={resetAutoTranspose} />

      {showPlugins && (
        <ViewSongPluginsPerSong
          song={song}
          songIndex={songIndex}
          onKeyChange={handleLocalKeyChange}
          onManualTranspose={onManualTranspose}
        />
      )}

      {/* As the first song's row has them: inside a service, so no popularity or updated badges */}
      <SongBadges
        song={song}
        className="horizontal-menu"
        extraBadges={hasChartsBadge(song) ? <SongChartsBadge song={song} from={fromUrl} /> : null}
        hideUpdatedBadge
        hidePopularityBadge
      />
    </div>
  );
};

export default SongHeaderSection;
